import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Share2, TrendingUp, Calendar, Target } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface PlatformStats {
  platform: string;
  shares: number;
  days_active: number;
  avg_shares_per_day: number;
}

interface ArticleShares {
  article_id: string;
  title: string;
  total_shares: number;
  platform_breakdown: Record<string, number>;
}

const COLORS = {
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  copy_link: '#10B981',
  native_share: '#8B5CF6',
  tiktok_clipboard: '#374151'
};

export default function ShareAnalytics() {
  const [platformStats, setPlatformStats] = useState<PlatformStats[]>([]);
  const [topArticles, setTopArticles] = useState<ArticleShares[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalShares, setTotalShares] = useState(0);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch platform statistics directly
      const { data: shareData } = await supabase
        .from('share_events')
        .select('platform, created_at')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      if (shareData) {
        const platformMap = new Map<string, Set<string>>();
        const platformCounts = new Map<string, number>();

        shareData.forEach(event => {
          const date = new Date(event.created_at).toDateString();
          
          if (!platformMap.has(event.platform)) {
            platformMap.set(event.platform, new Set());
            platformCounts.set(event.platform, 0);
          }
          
          platformMap.get(event.platform)!.add(date);
          platformCounts.set(event.platform, (platformCounts.get(event.platform) || 0) + 1);
        });

        const processedStats = Array.from(platformCounts.entries()).map(([platform, shares]) => ({
          platform,
          shares,
          days_active: platformMap.get(platform)!.size,
          avg_shares_per_day: shares / platformMap.get(platform)!.size
        })).sort((a, b) => b.shares - a.shares);

        setPlatformStats(processedStats);
        setTotalShares(shareData.length);
      }

      // Fetch top shared articles
      const { data: articleData } = await supabase
        .from('share_events')
        .select('article_id, title, platform')
        .not('article_id', 'is', null)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      if (articleData) {
        const articleMap = new Map<string, { title: string; platforms: Map<string, number> }>();
        
        articleData.forEach(event => {
          if (!articleMap.has(event.article_id!)) {
            articleMap.set(event.article_id!, {
              title: event.title || 'Untitled',
              platforms: new Map()
            });
          }
          
          const article = articleMap.get(event.article_id!)!;
          article.platforms.set(event.platform, (article.platforms.get(event.platform) || 0) + 1);
        });

        const articles: ArticleShares[] = Array.from(articleMap.entries())
          .map(([article_id, data]) => ({
            article_id,
            title: data.title,
            total_shares: Array.from(data.platforms.values()).reduce((sum, count) => sum + count, 0),
            platform_breakdown: Object.fromEntries(data.platforms)
          }))
          .sort((a, b) => b.total_shares - a.total_shares)
          .slice(0, 5);

        setTopArticles(articles);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const chartData = platformStats.map(stat => ({
    name: stat.platform.replace('_', ' '),
    shares: stat.shares
  }));

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Share Analytics</h1>
        <p className="text-muted-foreground">
          Last 30 days of social sharing performance
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Shares</CardTitle>
            <Share2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalShares}</div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platforms Used</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{platformStats.length}</div>
            <p className="text-xs text-muted-foreground">
              Active platforms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg per Day</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(totalShares / 30).toFixed(1)}
            </div>
            <p className="text-xs text-muted-foreground">
              Daily average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Platform</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {platformStats[0]?.platform.replace('_', ' ') || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {platformStats[0]?.shares || 0} shares
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Shares by Platform</CardTitle>
            <CardDescription>Distribution across social platforms</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="shares" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Platform Distribution</CardTitle>
            <CardDescription>Share percentage by platform</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="shares"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS] || '#8884d8'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Articles */}
      <Card>
        <CardHeader>
          <CardTitle>Top Shared Articles</CardTitle>
          <CardDescription>Most shared content in the last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topArticles.map((article, idx) => (
              <div key={article.article_id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">#{idx + 1}. {article.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {Object.entries(article.platform_breakdown).map(([platform, count]) => (
                      <span key={platform} className="mr-3">
                        {platform.replace('_', ' ')}: {count}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-2xl font-bold">{article.total_shares}</div>
              </div>
            ))}
            {topArticles.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No article shares recorded yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
